// ABOUTME: Drag-and-drop for the chat, the one drop path for the conversation and its composer:
// files dropped anywhere on it (photos, videos, recordings, documents) are attached exactly as
// the composer's + button attaches them.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Upload } from 'lucide-react'

const hasFiles = (event: React.DragEvent) => event.dataTransfer.types.includes('Files')

// A drag over the zone fires dragover at least every 350 ± 200 ms, even when the pointer is
// still (HTML drag-and-drop processing model). With none for this long the drag has ended
// some way the zone was not told about: cancelled, left the window, or dropped elsewhere.
const DRAG_LOST_MS = 1000

export function FileDropZone({
  onFiles,
  children,
}: {
  onFiles: (files: File[]) => void
  children: ReactNode
}) {
  const [dragging, setDragging] = useState(false)
  const zoneRef = useRef<HTMLDivElement>(null)
  const lostTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hide = useCallback(() => {
    if (lostTimer.current) clearTimeout(lostTimer.current)
    lostTimer.current = null
    setDragging(false)
  }, [])

  const show = useCallback(() => {
    if (lostTimer.current) clearTimeout(lostTimer.current)
    lostTimer.current = setTimeout(hide, DRAG_LOST_MS)
    setDragging(true)
  }, [hide])

  // Any drop or drag end anywhere on the page, or Esc, clears the overlay.
  useEffect(() => {
    if (!dragging) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide()
    }
    window.addEventListener('drop', hide, true)
    window.addEventListener('dragend', hide, true)
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('drop', hide, true)
      window.removeEventListener('dragend', hide, true)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [dragging, hide])

  useEffect(
    () => () => {
      if (lostTimer.current) clearTimeout(lostTimer.current)
    },
    []
  )

  const onEnterOrOver = (event: React.DragEvent) => {
    if (!hasFiles(event)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    show()
  }

  return (
    <div
      ref={zoneRef}
      className="relative flex min-h-0 min-w-0 flex-1 flex-col"
      data-testid="chat-drop-zone"
      onDragEnter={onEnterOrOver}
      onDragOver={onEnterOrOver}
      onDragLeave={(event) => {
        // Moving onto another element of the zone fires dragleave too; only leaving the zone
        // or the window ends it. Safari never names the element entered, so without one the
        // pointer's position decides.
        const zone = zoneRef.current
        if (!zone) return
        const next = event.relatedTarget
        if (next instanceof Node) {
          if (!zone.contains(next)) hide()
          return
        }
        const { left, right, top, bottom } = zone.getBoundingClientRect()
        const { clientX: x, clientY: y } = event
        if (x <= left || x >= right || y <= top || y >= bottom) hide()
      }}
      onDrop={(event) => {
        if (!hasFiles(event)) return
        event.preventDefault()
        hide()
        const files = Array.from(event.dataTransfer.files)
        if (files.length) onFiles(files)
      }}
    >
      {children}
      {dragging && (
        <div
          className="border-primary bg-background/85 pointer-events-none absolute inset-2 z-50 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed"
          data-testid="chat-drop-overlay"
        >
          <Upload className="text-primary size-8" />
          <p className="text-sm font-medium">Drop photos, videos or files to attach them</p>
        </div>
      )}
    </div>
  )
}
